import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../utils/api";
import ToggleSwitch from "./printers/ToggleSwitch";

const inputClass =
  "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

const RejectReasonsSection = () => {
  const [reasons, setReasons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newReason, setNewReason] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const sorted = useMemo(() => {
    const list = Array.isArray(reasons) ? [...reasons] : [];
    list.sort((a, b) =>
      (a?.reason || "").localeCompare(b?.reason || "", undefined, {
        sensitivity: "base",
      }),
    );
    return list;
  }, [reasons]);

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/settings/reject-reasons/all");
      const list = Array.isArray(res.data?.reasons) ? res.data.reasons : [];
      setReasons(list);
    } catch (err) {
      setReasons([]);
      toast.error("Failed to load reject reasons");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditingText(item.reason || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!editingId || !text || isSaving) return;
    setIsSaving(true);
    try {
      const res = await api.patch(`/settings/reject-reasons/${editingId}`, {
        reason: text,
      });
      const updated = res.data?.reason;
      if (updated?._id) {
        setReasons((prev) =>
          (prev || []).map((r) => (r._id === updated._id ? updated : r)),
        );
      } else {
        await loadAll();
      }
      toast.success("Reason updated");
      cancelEdit();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to update reason text";
      toast.error(msg);
      await loadAll();
      cancelEdit();
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (id, nextEnabled) => {
    if (!id || isSaving) return;
    setIsSaving(true);
    try {
      const res = await api.patch(`/settings/reject-reasons/${id}`, {
        isEnabled: !!nextEnabled,
      });

      const updated = res.data?.reason;
      if (updated?._id) {
        setReasons((prev) =>
          (prev || []).map((r) => (r._id === updated._id ? updated : r)),
        );
      } else {
        await loadAll();
      }
    } catch (err) {
      toast.error("Failed to update reason");
      await loadAll();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = async () => {
    const reason = newReason.trim();
    if (!reason || isSaving) return;
    setIsSaving(true);
    try {
      const res = await api.post("/settings/reject-reasons", { reason });
      const created = res.data?.reason;
      toast.success("Reason added");
      setNewReason("");

      if (created?._id) {
        setReasons((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const exists = list.some((r) => r._id === created._id);
          return exists ? list : [created, ...list];
        });
      } else {
        await loadAll();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to add reject reason";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-white flex-shrink-0">
          <span className="text-xs font-semibold">3</span>
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-900">
            Reject Reasons
          </h2>
          <p className="text-xs text-gray-500">
            Manage reasons shown when rejecting an order.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
            Add new reason
          </label>
          <input
            type="text"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="e.g. Kitchen closed"
            className={inputClass}
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={isSaving || !newReason.trim()}
          className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-semibold active:scale-[0.98] transition-all duration-200 ${
            isSaving || !newReason.trim()
              ? "opacity-70 cursor-not-allowed"
              : ""
          }`}
        >
          + Add Reason
        </button>
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
            <p className="text-sm font-semibold text-gray-700">
              Loading reasons...
            </p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-sm font-semibold text-gray-700">
              No reject reasons found
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Add a reason above to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((r) => (
              <div
                key={r._id}
                className="rounded-2xl border border-gray-100 bg-white shadow-sm px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  {editingId === r._id ? (
                    <>
                      <input
                        type="text"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveEdit();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelEdit();
                          }
                        }}
                        className={inputClass}
                        autoFocus
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Press Enter to save, Esc to cancel.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-gray-900 break-words">
                        {r.reason}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {r.isEnabled ? "Enabled" : "Disabled"}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 sm:self-auto self-end">
                  <button
                    type="button"
                    onClick={() =>
                      editingId === r._id ? cancelEdit() : startEdit(r)
                    }
                    className="px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700 text-xs font-semibold hover:bg-gray-100 transition"
                  >
                    {editingId === r._id ? "Cancel" : "Edit"}
                  </button>
                  <ToggleSwitch
                    checked={!!r.isEnabled}
                    onChange={(val) => handleToggle(r._id, val)}
                    labelOn="Enabled"
                    labelOff="Disabled"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RejectReasonsSection;

